import { getApiLanguage } from '../config/apiLanguage';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.VITE_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://api.visime.com');

export type SocialLoginNeedsBirthday = {
  kind: 'needs_birthday';
  token: string;
  userId: number;
  email?: string;
  appleId?: string;
  title?: string;
  message?: string;
};

export type SocialLoginSuccess = {
  kind: 'success';
  token: string;
  userId: number;
  email?: string;
  firstName?: string;
  lastName?: string;
};

export type SocialLoginFailure = {
  kind: 'error';
  title?: string;
  message: string;
  httpStatus?: number;
};

export type SocialLoginResult = SocialLoginNeedsBirthday | SocialLoginSuccess | SocialLoginFailure;

function decodeJwtPayload(token: string): Record<string, unknown> {
  const segment = token.split('.')[1];
  if (!segment) {
    throw new Error('Invalid ID token');
  }
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  const json = atob(padded);
  return JSON.parse(json) as Record<string, unknown>;
}

export function appleUserIdFromIdToken(idToken: string): string {
  const payload = decodeJwtPayload(idToken);
  const sub = payload.sub;
  if (typeof sub !== 'string' || !sub) {
    throw new Error('Apple ID token missing subject');
  }
  return sub;
}

function readUserId(data: Record<string, unknown>): number | null {
  const user = data.user as Record<string, unknown> | undefined;
  const raw = user?.id ?? data.user_id ?? data.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    const parsed = parseInt(raw, 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function parseSocialLoginResponse(
  data: Record<string, unknown>,
  httpStatus: number
): SocialLoginResult {
  const token = typeof data.token === 'string' ? data.token : '';
  const userId = readUserId(data);
  const title = typeof data.title === 'string' ? data.title : undefined;
  const message =
    typeof data.message === 'string'
      ? data.message
      : typeof data.error === 'string'
        ? data.error
        : 'Login failed';

  if (httpStatus === 401 || data.error === 'User is blocked') {
    return { kind: 'error', title, message, httpStatus };
  }

  if (token && userId && (httpStatus === 400 || title)) {
    const user = data.user as Record<string, unknown> | undefined;
    return {
      kind: 'needs_birthday',
      token,
      userId,
      email: typeof user?.email === 'string' ? user.email : undefined,
      appleId: typeof user?.apple_id === 'string' ? user.apple_id : undefined,
      title,
      message,
    };
  }

  if (token && userId && httpStatus >= 200 && httpStatus < 300) {
    const user = data.user as Record<string, unknown> | undefined;
    return {
      kind: 'success',
      token,
      userId,
      email: typeof user?.email === 'string' ? user.email : undefined,
      firstName:
        typeof user?.firstname === 'string'
          ? user.firstname
          : typeof user?.firstName === 'string'
            ? user.firstName
            : undefined,
      lastName:
        typeof user?.lastname === 'string'
          ? user.lastname
          : typeof user?.lastName === 'string'
            ? user.lastName
            : undefined,
    };
  }

  return { kind: 'error', title, message, httpStatus };
}

async function postSocial(path: string, fields: Record<string, string>): Promise<SocialLoginResult> {
  const body = new URLSearchParams();
  Object.entries(fields).forEach(([key, value]) => {
    if (value) body.append(key, value);
  });
  body.append('language', getApiLanguage());

  const response = await fetch(`${API_BASE_URL}/api/v1.1/${path}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  });

  let data: Record<string, unknown> = {};
  try {
    data = (await response.json()) as Record<string, unknown>;
  } catch {
    data = {};
  }

  return parseSocialLoginResponse(data, response.status);
}

export async function googleLogin2(params: {
  email: string;
  name: string;
  lastName?: string;
}): Promise<SocialLoginResult> {
  return postSocial('googleLogin2', {
    email: params.email,
    name: params.name,
    last_name: params.lastName || '',
  });
}

export async function appleLogin(params: {
  appleId: string;
  email: string;
  name: string;
  lastName?: string;
}): Promise<SocialLoginResult> {
  return postSocial('appleLogin', {
    apple_id: params.appleId,
    email: params.email,
    name: params.name,
    last_name: params.lastName || '',
  });
}

/** dd.MM.yyyy — matches iOS `vcBirthdayPopup`. */
export function formatBirthdayForSocialApi(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) {
    throw new Error('Invalid date');
  }
  return `${day}.${month}.${year}`;
}

export async function submitGoogleBirthday(
  email: string,
  birthdayIso: string
): Promise<SocialLoginSuccess | SocialLoginFailure> {
  const body = new URLSearchParams();
  body.append('email', email);
  body.append('birthday', formatBirthdayForSocialApi(birthdayIso));

  const response = await fetch(`${API_BASE_URL}/api/v1.1/googleBirthday2`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  });

  const data = (await response.json()) as Record<string, unknown>;
  const parsed = parseSocialLoginResponse(data, response.status);
  if (parsed.kind === 'success') return parsed;
  if (parsed.kind === 'needs_birthday') {
    return {
      kind: 'error',
      title: parsed.title,
      message: parsed.message || 'Birthday required',
      httpStatus: response.status,
    };
  }
  return parsed;
}

export async function submitAppleBirthday(
  appleId: string,
  birthdayIso: string,
  email?: string
): Promise<SocialLoginSuccess | SocialLoginFailure> {
  const body = new URLSearchParams();
  if (email) body.append('email', email);
  body.append('apple_id', appleId);
  body.append('birthday', formatBirthdayForSocialApi(birthdayIso));

  const response = await fetch(`${API_BASE_URL}/api/v1.1/appleBirthday`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  });

  const data = (await response.json()) as Record<string, unknown>;
  const token = typeof data.token === 'string' ? data.token : '';
  const userId = readUserId(data);
  const message = typeof data.message === 'string' ? data.message : '';
  const title = typeof data.title === 'string' ? data.title : undefined;

  if (response.ok && token && userId && message === 'Success') {
    return {
      kind: 'success',
      token,
      userId,
      email: typeof data.user_email === 'string' ? data.user_email : email,
      firstName: typeof data.user_firstname === 'string' ? data.user_firstname : undefined,
    };
  }

  if (token && userId && response.ok) {
    return { kind: 'success', token, userId, email };
  }

  const errMessage =
    typeof data.message === 'string'
      ? data.message
      : typeof data.error === 'string'
        ? data.error
        : 'Could not save birthday';
  return { kind: 'error', title, message: errMessage, httpStatus: response.status };
}
